/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import axios from 'axios';
import {error} from '@ion/core/src/logger/logger';
import {GenericObject} from '@ion/core/src/types/entities';
import {preloadImage} from '../img/ImageLoader';
import {
  BACKGROUND_IMG_PATH,
  CURRENT_BACKGROUND_IMG_PATH,
  CUSTOM_HEADER_LOGO_PATH,
  CUSTOM_LOGO_PATH,
  CUSTOM_THEME_PATH
} from './theme.config';

/**
 * The `loadExternalTheme` function allows for retrieving an external theme.json
 * document for subsequent use in the DLS theme.
 *
 * Generally, this function is not used directly, but rather, it is used by both
 * the `createExternalTheme` API and `useExternalTheme` hook in order to both
 * retrieve the theme and create custom theme based on the externally defined
 * json theme. Thus, clients should typically not need to use this function
 * directly.
 *
 * ```typescript
 *
 *   import {loadExternalTheme} from '@ion/dls/src/theme/ThemeLoader';
 *
 *   const loadTheme = async(): Promise<GenericObject> => {
 *     const json = await loadExternalTheme(url);
 *     return json;
 *   };
 *
 * ```
 *
 * @see     theme.ts
 * @see     theme.json5
 * @category theme
 *
 * @param   url The url from which the external theme is to be loaded. Defaults
 *          to '/theme.json'.
 * @returns The theme json document retrieved from the given url.
 */
export const loadExternalTheme = async(url?: string): Promise<GenericObject> => {
  try {
    const response = await axios.get(url || resolve(CUSTOM_THEME_PATH));
    return response.data;
  } catch (err) {
    error(`An Error occurred while attempting to load the External Theme from ${url}`);
  }
};

/**
 * Provides a utility which preloads custom theme resources, specifically the
 * custom theme logo and background image (if any).
 *
 * Generally, this function is not used directly, but rather, it is used by
 * the `useExternalTheme` hook in order to preload theme based assets.
 *
 * @returns The theme json document retrieved from the given url.
 */
export const loadExternalThemeAssets = async(): Promise<boolean> => {
  await preloadImage(
    getExternalBackgroundImageURL(), getExternalLogoImageURL()
  );
  return true;
};

/**
 * Provides a utility for retrieving the custom background image URL from which
 * the white labelled partner background page image is to be retrieved.
 *
 * Generally, the external background image is preloaded during initialization
 * via an implicit call to `loadExternalThemeAssets`, however, when referencing
 * the background image, this function should be used exclusively in order to
 * ensure the image is loaded via the fully qualified URL.
 *
 * @returns the fully qualified URL to the external background image.
 */
export const getExternalBackgroundImageURL = (): string => (
  resolve(BACKGROUND_IMG_PATH)
);

/**
 * Provides a utility for retrieving the current theme login background image
 * URL. This image is not white labelled; resellers should continue to use the
 * image resolved by `getExternalBackgroundImageURL`.
 *
 * ```typescript
 *   import {getCurrentBackgroundImageURL} from '@ion/dls/src/theme/ThemeLoader';
 *   ...
 *
 *   const url = getCurrentBackgroundImageURL();
 * ```
 *
 * @returns the fully qualified URL to the current theme background image.
 */
export const getCurrentBackgroundImageURL = (): string => (
  resolve(CURRENT_BACKGROUND_IMG_PATH)
);

/**
 * Provides a utility for retrieving the custom logo URL from which the white
 * labelled partner logo image is to be retrieved.
 *
 * Generally, the external customer logo image is preloaded during initialization
 * via an implicit call to `loadExternalThemeAssets`, however, when referencing
 * the background image, this function should be used exclusively in order to
 * ensure the image is loaded via the fully qualified URL.
 *
 * @returns the fully qualified URL to the external customer logo image.
 */
export const getExternalLogoImageURL = (): string => (
  resolve(CUSTOM_LOGO_PATH)
);

/**
 * Provides a utility for retrieving the custom header logo URL from which the
 * white labelled partner header logo image is to be retrieved.
 *
 * The header logo is a smaller-framed variant of the standard logo intended for
 * the fixed-height application header. Resellers that do not provide a header
 * variant fall back to the standard logo via the consuming component, so the
 * returned URL may resolve to a non-existent asset.
 *
 * @returns the fully qualified URL to the external customer header logo image.
 */
export const getExternalHeaderLogoImageURL = (): string => (
  resolve(CUSTOM_HEADER_LOGO_PATH)
);

/**
 * Provides a local convenience function for resolving the fully qualified URL
 * for the given path.
 */
const resolve = (uri: string): string => {
  const origin = globalThis?.location?.origin || window?.location.origin || '/';
  return `${origin}/${uri}`;
};

export default loadExternalTheme;
